import React from 'react';
import { DraftSaveStatus } from '../../services/draftService';

interface SaveStatusIndicatorProps {
  status: DraftSaveStatus;
  lastSaved: Date | null;
  onRetry?: () => void;
}

/**
 * Displays the current draft save status with visual feedback
 */
export const SaveStatusIndicator: React.FC<SaveStatusIndicatorProps> = ({
  status,
  lastSaved,
  onRetry,
}) => {
  const formatLastSaved = (date: Date | string | number): string => {
    const dateObj = date instanceof Date ? date : new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    
    if (diffSecs < 10) return 'just now';
    if (diffSecs < 60) return `${diffSecs}s ago`;
    if (diffMins < 60) return `${diffMins}m ago`;
    
    return dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (status === 'idle' && !lastSaved) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 text-sm">
      {status === 'saving' && (
        <>
          <svg 
            className="animate-spin h-4 w-4 text-gray-500" 
            xmlns="http://www.w3.org/2000/svg" 
            fill="none" 
            viewBox="0 0 24 24"
          >
            <circle 
              className="opacity-25" 
              cx="12" 
              cy="12" 
              r="10" 
              stroke="currentColor" 
              strokeWidth="4"
            />
            <path 
              className="opacity-75" 
              fill="currentColor" 
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span className="text-gray-500">Saving...</span>
        </>
      )}

      {status === 'saved' && lastSaved && (
        <>
          <svg 
            className="h-4 w-4 text-green-500" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M5 13l4 4L19 7" 
            />
          </svg>
          <span className="text-gray-500">
            Saved {formatLastSaved(lastSaved)}
          </span>
        </>
      )}

      {status === 'error' && (
        <>
          <svg 
            className="h-4 w-4 text-red-500" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
            />
          </svg>
          <span className="text-red-500">Save failed</span>
          {onRetry && (
            <button
              onClick={onRetry}
              className="text-primary-500 hover:text-primary-600 underline"
            >
              Retry
            </button>
          )}
        </>
      )}

      {status === 'idle' && lastSaved && (
        <>
          <svg 
            className="h-4 w-4 text-gray-400" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M5 13l4 4L19 7" 
            />
          </svg>
          <span className="text-gray-400">
            Saved {formatLastSaved(lastSaved)}
          </span>
        </>
      )}
    </div>
  );
};
